/**
 * Scoring Framework Proposal
 *
 * Hidden page for clinician validation of the scoring approach.
 * Not linked from main navigation — access via /proposal
 *
 * Last Updated: 2026-02-06 (EST)
 */

import { QuestionMarkCircleIcon } from "@heroicons/react/24/outline";

/* ─────────────────────────────────────────────
   SVG Illustrations for each deficit type
   Simple visual metaphors so clinicians can
   immediately see what each condition represents
   ───────────────────────────────────────────── */

function IllustrationBlur() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			<text x="10" y="38" fontSize="28" fontFamily="serif" fill="#1e293b">
				Hello
			</text>
			<text
				x="70"
				y="38"
				fontSize="28"
				fontFamily="serif"
				fill="#94a3b8"
				filter="url(#blur)"
			>
				Hello
			</text>
			<defs>
				<filter id="blur">
					<feGaussianBlur stdDeviation="2" />
				</filter>
			</defs>
			<text x="10" y="55" fontSize="8" fill="#64748b">
				Clear
			</text>
			<text x="75" y="55" fontSize="8" fill="#64748b">
				Blurred
			</text>
		</svg>
	);
}

function IllustrationContrast() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			<rect x="5" y="5" width="50" height="40" rx="6" fill="#f1f5f9" />
			<circle cx="20" cy="25" r="8" fill="#1e293b" />
			<circle cx="40" cy="25" r="8" fill="#cbd5e1" />
			<rect x="65" y="5" width="50" height="40" rx="6" fill="#f1f5f9" />
			<circle cx="80" cy="25" r="8" fill="#94a3b8" />
			<circle cx="100" cy="25" r="8" fill="#cbd5e1" />
			<text x="10" y="55" fontSize="8" fill="#64748b">
				Easy to tell apart
			</text>
			<text x="70" y="55" fontSize="8" fill="#64748b">
				Hard to tell apart
			</text>
		</svg>
	);
}

function IllustrationScotoma() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			<rect x="5" y="5" width="110" height="45" rx="6" fill="#e0f2fe" />
			<line x1="15" y1="15" x2="105" y2="15" stroke="#93c5fd" strokeWidth="1" />
			<line x1="15" y1="25" x2="105" y2="25" stroke="#93c5fd" strokeWidth="1" />
			<line x1="15" y1="35" x2="105" y2="35" stroke="#93c5fd" strokeWidth="1" />
			<circle cx="55" cy="25" r="14" fill="#1e293b" opacity="0.85" />
			<circle cx="90" cy="15" r="7" fill="#1e293b" opacity="0.6" />
			<text x="46" y="28" fontSize="7" fill="white" fontWeight="bold">
				blind
			</text>
			<text x="37" y="55" fontSize="8" fill="#64748b">
				Dark patches in vision
			</text>
		</svg>
	);
}

function IllustrationDistortion() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			{/* Straight grid */}
			<line x1="10" y1="10" x2="10" y2="50" stroke="#93c5fd" strokeWidth="1" />
			<line x1="25" y1="10" x2="25" y2="50" stroke="#93c5fd" strokeWidth="1" />
			<line x1="40" y1="10" x2="40" y2="50" stroke="#93c5fd" strokeWidth="1" />
			<line x1="10" y1="10" x2="40" y2="10" stroke="#93c5fd" strokeWidth="1" />
			<line x1="10" y1="30" x2="40" y2="30" stroke="#93c5fd" strokeWidth="1" />
			<line x1="10" y1="50" x2="40" y2="50" stroke="#93c5fd" strokeWidth="1" />
			{/* Warped grid */}
			<path d="M70,10 L70,50" stroke="#f97316" strokeWidth="1" fill="none" />
			<path
				d="M85,10 Q90,30 85,50"
				stroke="#f97316"
				strokeWidth="1"
				fill="none"
			/>
			<path
				d="M100,10 Q92,30 100,50"
				stroke="#f97316"
				strokeWidth="1"
				fill="none"
			/>
			<path d="M70,10 L100,10" stroke="#f97316" strokeWidth="1" fill="none" />
			<path
				d="M70,30 Q85,25 100,30"
				stroke="#f97316"
				strokeWidth="1"
				fill="none"
			/>
			<path d="M70,50 L100,50" stroke="#f97316" strokeWidth="1" fill="none" />
			<text x="12" y="58" fontSize="7" fill="#64748b">
				Normal
			</text>
			<text x="72" y="58" fontSize="7" fill="#64748b">
				Distorted
			</text>
		</svg>
	);
}

function IllustrationMetamorphopsia() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			{/* Straight lines */}
			<line x1="10" y1="15" x2="50" y2="15" stroke="#1e293b" strokeWidth="2" />
			<line x1="10" y1="30" x2="50" y2="30" stroke="#1e293b" strokeWidth="2" />
			<line x1="10" y1="45" x2="50" y2="45" stroke="#1e293b" strokeWidth="2" />
			{/* Wavy lines */}
			<path
				d="M70,15 Q80,10 90,15 Q100,20 110,15"
				stroke="#dc2626"
				strokeWidth="2"
				fill="none"
			/>
			<path
				d="M70,30 Q80,25 90,30 Q100,35 110,30"
				stroke="#dc2626"
				strokeWidth="2"
				fill="none"
			/>
			<path
				d="M70,45 Q80,40 90,45 Q100,50 110,45"
				stroke="#dc2626"
				strokeWidth="2"
				fill="none"
			/>
			<text x="15" y="55" fontSize="7" fill="#64748b">
				Straight
			</text>
			<text x="75" y="55" fontSize="7" fill="#64748b">
				Wavy
			</text>
		</svg>
	);
}

function IllustrationDiplopia() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			{/* Single circle */}
			<circle cx="25" cy="28" r="15" fill="#3b82f6" />
			{/* Double circles */}
			<circle cx="82" cy="25" r="15" fill="#3b82f6" opacity="0.5" />
			<circle cx="92" cy="31" r="15" fill="#3b82f6" opacity="0.5" />
			<text x="12" y="55" fontSize="7" fill="#64748b">
				Single image
			</text>
			<text x="72" y="55" fontSize="7" fill="#64748b">
				Double image
			</text>
		</svg>
	);
}

function IllustrationAnisoconia() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			{/* Same-sized pair */}
			<circle
				cx="20"
				cy="25"
				r="12"
				fill="none"
				stroke="#1e293b"
				strokeWidth="2"
			/>
			<circle
				cx="45"
				cy="25"
				r="12"
				fill="none"
				stroke="#1e293b"
				strokeWidth="2"
			/>
			{/* Different-sized pair */}
			<circle
				cx="80"
				cy="25"
				r="12"
				fill="none"
				stroke="#dc2626"
				strokeWidth="2"
			/>
			<circle
				cx="105"
				cy="25"
				r="18"
				fill="none"
				stroke="#dc2626"
				strokeWidth="2"
			/>
			<text x="12" y="52" fontSize="7" fill="#64748b">
				Same size
			</text>
			<text x="72" y="52" fontSize="7" fill="#64748b">
				Different sizes
			</text>
		</svg>
	);
}

function IllustrationColorShift() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			{/* Normal colors */}
			<circle cx="15" cy="25" r="10" fill="#ef4444" />
			<circle cx="35" cy="25" r="10" fill="#22c55e" />
			<circle cx="55" cy="25" r="10" fill="#3b82f6" />
			{/* Shifted colors */}
			<circle cx="80" cy="25" r="10" fill="#ca8a04" />
			<circle cx="100" cy="25" r="10" fill="#a3a3a3" />
			<text x="15" y="50" fontSize="7" fill="#64748b">
				True colors
			</text>
			<text x="75" y="50" fontSize="7" fill="#64748b">
				Shifted / muted
			</text>
		</svg>
	);
}

function IllustrationRotation() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			<rect
				x="10"
				y="10"
				width="35"
				height="35"
				rx="3"
				fill="none"
				stroke="#1e293b"
				strokeWidth="2"
			/>
			<line x1="10" y1="48" x2="45" y2="48" stroke="#94a3b8" strokeWidth="1" />
			<g transform="rotate(15, 90, 27)">
				<rect
					x="72"
					y="10"
					width="35"
					height="35"
					rx="3"
					fill="none"
					stroke="#dc2626"
					strokeWidth="2"
				/>
			</g>
			<line x1="70" y1="48" x2="110" y2="48" stroke="#94a3b8" strokeWidth="1" />
			<text x="15" y="58" fontSize="7" fill="#64748b">
				Level
			</text>
			<text x="78" y="58" fontSize="7" fill="#64748b">
				Tilted
			</text>
		</svg>
	);
}

function IllustrationBrightness() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			<rect x="5" y="5" width="35" height="35" rx="6" fill="#fef9c3" />
			<circle cx="22" cy="22" r="10" fill="#facc15" />
			{/* rays */}
			<line
				x1="22"
				y1="8"
				x2="22"
				y2="4"
				stroke="#facc15"
				strokeWidth="2"
				strokeLinecap="round"
			/>
			<line
				x1="32"
				y1="12"
				x2="35"
				y2="9"
				stroke="#facc15"
				strokeWidth="2"
				strokeLinecap="round"
			/>
			<line
				x1="12"
				y1="12"
				x2="9"
				y2="9"
				stroke="#facc15"
				strokeWidth="2"
				strokeLinecap="round"
			/>

			<rect x="50" y="5" width="35" height="35" rx="6" fill="#f1f5f9" />
			<circle cx="67" cy="22" r="10" fill="#cbd5e1" />

			<rect x="95" y="5" width="20" height="35" rx="6" fill="#1e293b" />
			<text x="8" y="52" fontSize="7" fill="#64748b">
				Normal
			</text>
			<text x="55" y="52" fontSize="7" fill="#64748b">
				Dim
			</text>
			<text x="96" y="52" fontSize="7" fill="#64748b">
				Dark
			</text>
		</svg>
	);
}

function IllustrationVacuum() {
	return (
		<svg viewBox="0 0 120 60" className="w-full h-16">
			{/* Normal grid */}
			<rect x="5" y="5" width="45" height="45" rx="4" fill="#f0fdf4" />
			<line x1="20" y1="5" x2="20" y2="50" stroke="#86efac" strokeWidth="0.5" />
			<line x1="35" y1="5" x2="35" y2="50" stroke="#86efac" strokeWidth="0.5" />
			<line x1="5" y1="20" x2="50" y2="20" stroke="#86efac" strokeWidth="0.5" />
			<line x1="5" y1="35" x2="50" y2="35" stroke="#86efac" strokeWidth="0.5" />
			{/* Sucked-in grid */}
			<rect x="65" y="5" width="50" height="45" rx="4" fill="#fef2f2" />
			<path
				d="M80,5 Q88,27 80,50"
				stroke="#fca5a5"
				strokeWidth="0.5"
				fill="none"
			/>
			<path
				d="M100,5 Q92,27 100,50"
				stroke="#fca5a5"
				strokeWidth="0.5"
				fill="none"
			/>
			<path
				d="M65,20 Q90,24 115,20"
				stroke="#fca5a5"
				strokeWidth="0.5"
				fill="none"
			/>
			<path
				d="M65,35 Q90,31 115,35"
				stroke="#fca5a5"
				strokeWidth="0.5"
				fill="none"
			/>
			<circle cx="90" cy="27" r="4" fill="#ef4444" opacity="0.3" />
			<text x="10" y="58" fontSize="7" fill="#64748b">
				Normal
			</text>
			<text x="72" y="58" fontSize="7" fill="#64748b">
				Pulled inward
			</text>
		</svg>
	);
}

/* ─────────────────────────────────────────────
   Deficit type definitions
   Plain English, simple formulas, visual per type
   ───────────────────────────────────────────── */

const deficitTypes: {
	id: string;
	title: string;
	whatItIs: string;
	realLife: string;
	illustration: () => React.ReactNode;
	howWeScore: string;
	examples: { label: string; score: number; note: string }[];
}[] = [
	{
		id: "blur",
		title: "Blurriness",
		whatItIs:
			"Everything looks out of focus, like looking through a foggy window.",
		realLife: "Difficulty reading signs, recognizing faces from a distance.",
		illustration: IllustrationBlur,
		howWeScore:
			"We measure how strong the blur is (0 = crystal clear, maximum = can't see anything). The stronger the blur, the higher the deficit.",
		examples: [
			{ label: "Crystal clear", score: 0, note: "20/20 vision" },
			{ label: "Slightly soft", score: 15, note: "Like needing new glasses" },
			{
				label: "Noticeably blurry",
				score: 40,
				note: "Hard to read small text",
			},
			{ label: "Very blurry", score: 70, note: "Can only see large shapes" },
			{
				label: "Completely blurred",
				score: 100,
				note: "Can't make out anything",
			},
		],
	},
	{
		id: "contrast",
		title: "Contrast Sensitivity",
		whatItIs:
			"Hard to tell things apart when the colors or brightness are similar. Like trying to see in fog or dim lighting.",
		realLife:
			"Trouble seeing steps, curbs, or faces in low light. Everything looks washed out.",
		illustration: IllustrationContrast,
		howWeScore:
			"We measure how much contrast is lost (0 = normal differences between light and dark, maximum = everything looks the same shade of gray).",
		examples: [
			{ label: "Normal contrast", score: 0, note: "Sharp differences" },
			{
				label: "Slightly washed out",
				score: 25,
				note: "Noticeable in dim light",
			},
			{ label: "Faded", score: 50, note: "Hard to see subtle details" },
			{ label: "Very flat", score: 75, note: "Most things blend together" },
			{
				label: "All gray",
				score: 100,
				note: "No difference between light and dark",
			},
		],
	},
	{
		id: "scotoma",
		title: "Blind Spots (Scotoma)",
		whatItIs:
			"Dark patches or missing areas in your vision. Some parts of the picture are just gone.",
		realLife:
			"Missing parts of words while reading, not seeing objects in certain areas.",
		illustration: IllustrationScotoma,
		howWeScore:
			"We measure how much of the visual field is blocked. A small spot off to the side is less severe than a large spot in the center. Central blind spots count much more because that's where you look directly.",
		examples: [
			{ label: "No blind spots", score: 0, note: "Full visual field" },
			{ label: "Tiny spot, off center", score: 10, note: "Barely noticeable" },
			{ label: "Medium spot, to the side", score: 30, note: "Noticeable gap" },
			{
				label: "Large spot, near center",
				score: 65,
				note: "Major gap in vision",
			},
			{
				label: "Center is blocked",
				score: 90,
				note: "Can't see what you look at",
			},
		],
	},
	{
		id: "distortion",
		title: "Distortion (Warping)",
		whatItIs:
			"Straight lines look bent or wavy. Shapes appear stretched, compressed, or twisted.",
		realLife:
			"Door frames look curved, tiles on the floor seem uneven, faces look lopsided.",
		illustration: IllustrationDistortion,
		howWeScore:
			"We measure how far things shift from where they should be. More shifting = higher score. Central distortion (what you're looking right at) counts more than edges.",
		examples: [
			{
				label: "No distortion",
				score: 0,
				note: "Straight lines look straight",
			},
			{ label: "Slight bend", score: 20, note: "Lines slightly off" },
			{ label: "Obvious warping", score: 50, note: "Clearly bent or twisted" },
			{ label: "Severe warping", score: 80, note: "Shapes are very distorted" },
			{ label: "Everything warped", score: 100, note: "Unrecognizable shapes" },
		],
	},
	{
		id: "metamorphopsia",
		title: "Wavy Vision (Metamorphopsia)",
		whatItIs:
			"Straight lines appear wavy or rippled, like looking through water or heat haze.",
		realLife:
			"Text on a page seems to swim, window blinds look wavy, edges of furniture ripple.",
		illustration: IllustrationMetamorphopsia,
		howWeScore:
			"We combine two things: how tall the waves are (amplitude) and how many waves there are (frequency). Bigger and more frequent waves = worse.",
		examples: [
			{ label: "Lines are straight", score: 0, note: "Normal" },
			{ label: "Slight ripple", score: 20, note: "Barely wavy" },
			{ label: "Clear waves", score: 50, note: "Like looking through water" },
			{ label: "Strong waves", score: 80, note: "Text is very hard to read" },
			{
				label: "Extreme",
				score: 100,
				note: "Everything looks like a funhouse mirror",
			},
		],
	},
	{
		id: "diplopia",
		title: "Double Vision (Diplopia)",
		whatItIs:
			"You see two images of the same thing, overlapping or side by side.",
		realLife:
			"Seeing two of everything, making it hard to reach for objects or read.",
		illustration: IllustrationDiplopia,
		howWeScore:
			"We measure how far apart the two images are and how much they overlap. Images that are far apart and barely overlap are worse than images close together.",
		examples: [
			{ label: "Single image", score: 0, note: "No doubling" },
			{
				label: "Slight ghost image",
				score: 20,
				note: "Faint second image nearby",
			},
			{
				label: "Clear double",
				score: 50,
				note: "Two distinct but overlapping images",
			},
			{ label: "Wide split", score: 80, note: "Two images far apart" },
			{
				label: "Fully separated",
				score: 100,
				note: "Two complete separate images",
			},
		],
	},
	{
		id: "anisoconia",
		title: "Size Difference (Anisoconia)",
		whatItIs:
			"One eye sees things bigger or smaller than the other. Objects appear different sizes depending on which eye is open.",
		realLife:
			"Feeling dizzy or off-balance, difficulty judging distances, headaches from eye strain.",
		illustration: IllustrationAnisoconia,
		howWeScore:
			"We measure how much the size difference is from normal (1.0 = same size in both eyes). Any deviation in either direction — bigger or smaller — counts as a deficit.",
		examples: [
			{ label: "Same size both eyes", score: 0, note: "No difference" },
			{
				label: "5% size difference",
				score: 10,
				note: "Barely noticeable, usually tolerated",
			},
			{
				label: "10% size difference",
				score: 20,
				note: "Noticeable, may cause strain",
			},
			{
				label: "25% size difference",
				score: 50,
				note: "Hard for the brain to merge images",
			},
			{
				label: "50% size difference",
				score: 100,
				note: "One eye sees things half the size",
			},
		],
	},
	{
		id: "color",
		title: "Color Shift",
		whatItIs:
			"Colors don't look right. Everything may appear tinted (yellowish, bluish) or colors are washed out and muted.",
		realLife:
			"Red and green are hard to tell apart, everything looks tinted yellow or blue, colors seem dull.",
		illustration: IllustrationColorShift,
		howWeScore:
			"We measure how far the colors shift from normal. A strong tint in any direction (blue, yellow, etc.) or very muted/saturated colors both increase the score.",
		examples: [
			{ label: "True colors", score: 0, note: "Colors look normal" },
			{ label: "Slight tint", score: 20, note: "Mild yellow or blue cast" },
			{ label: "Strong tint", score: 50, note: "Clearly tinted, colors off" },
			{ label: "Very distorted", score: 80, note: "Hard to identify colors" },
			{
				label: "No color",
				score: 100,
				note: "Everything looks gray (monochrome)",
			},
		],
	},
	{
		id: "rotation",
		title: "Tilted Vision (Rotation)",
		whatItIs:
			"The whole world appears tilted or rotated. Horizontal lines look slanted.",
		realLife:
			"Feeling like the room is tilted, difficulty with balance and spatial orientation.",
		illustration: IllustrationRotation,
		howWeScore:
			"We measure how many degrees the image is rotated from level. A small tilt is mild; a large tilt is severe.",
		examples: [
			{ label: "Level", score: 0, note: "Horizon is straight" },
			{ label: "Slight tilt (5 deg)", score: 11, note: "Barely off" },
			{ label: "Noticeable tilt (15 deg)", score: 33, note: "Clearly slanted" },
			{ label: "Strong tilt (30 deg)", score: 67, note: "Very disorienting" },
			{ label: "Extreme tilt (45 deg)", score: 100, note: "World on its side" },
		],
	},
	{
		id: "brightness",
		title: "Brightness / Light Sensitivity",
		whatItIs:
			"The world appears too bright, too dim, or magnified. The overall intensity of light is different from normal.",
		realLife:
			"Things seem washed out by glare, or too dark to see properly. Images may appear zoomed in or out.",
		illustration: IllustrationBrightness,
		howWeScore:
			"We measure how far the brightness or magnification is from normal (1.0). Too bright or too dim both count as a deficit.",
		examples: [
			{ label: "Normal brightness", score: 0, note: "Comfortable light level" },
			{ label: "Slightly off", score: 20, note: "A bit too bright or dim" },
			{ label: "Uncomfortable", score: 50, note: "Clearly too bright or dark" },
			{ label: "Severe", score: 80, note: "Painful glare or very dark" },
			{
				label: "Extreme",
				score: 100,
				note: "Blinding light or total darkness",
			},
		],
	},
	{
		id: "vacuum",
		title: "Vacuum Effect (Spatial Pull)",
		whatItIs:
			"Parts of the image appear pulled or sucked inward toward a point, like looking through a lens with a dent.",
		realLife:
			"Objects near the center of vision appear compressed or warped, while edges look stretched.",
		illustration: IllustrationVacuum,
		howWeScore:
			"Same approach as distortion — we measure how far things get pulled from where they should be. More pull = higher score.",
		examples: [
			{
				label: "No distortion",
				score: 0,
				note: "Everything in its right place",
			},
			{ label: "Slight pull", score: 20, note: "Minor inward bend" },
			{
				label: "Clear pull",
				score: 50,
				note: "Noticeable spatial compression",
			},
			{ label: "Strong pull", score: 80, note: "Severe inward warping" },
			{
				label: "Extreme",
				score: 100,
				note: "Image appears collapsed at center",
			},
		],
	},
];

/* ─────────────────────────────────────────────
   Severity bands — full range from perfect to blind
   ───────────────────────────────────────────── */

const severityBands = [
	{
		range: "95 – 100%",
		label: "Perfect / Near-Perfect",
		description: "No meaningful deficits. The patient sees normally.",
		color: "bg-emerald-500",
		textColor: "text-emerald-700",
		bgLight: "bg-emerald-50",
	},
	{
		range: "80 – 94%",
		label: "Mild",
		description:
			"Small deficits present but the patient can compensate. Daily life is mostly unaffected.",
		color: "bg-lime-500",
		textColor: "text-lime-700",
		bgLight: "bg-lime-50",
	},
	{
		range: "60 – 79%",
		label: "Moderate",
		description:
			"Noticeable impact on daily activities. Reading, driving, or recognizing faces may be difficult.",
		color: "bg-yellow-500",
		textColor: "text-yellow-700",
		bgLight: "bg-yellow-50",
	},
	{
		range: "40 – 59%",
		label: "Severe",
		description:
			"Significant vision loss. Major difficulty with most visual tasks. Likely needs assistance.",
		color: "bg-orange-500",
		textColor: "text-orange-700",
		bgLight: "bg-orange-50",
	},
	{
		range: "20 – 39%",
		label: "Profound",
		description:
			"Very little useful vision remains. Can detect light, movement, or large shapes but not details.",
		color: "bg-red-500",
		textColor: "text-red-700",
		bgLight: "bg-red-50",
	},
	{
		range: "1 – 19%",
		label: "Near-Blind",
		description:
			"Almost no functional vision. May only perceive light or vague shadows.",
		color: "bg-red-800",
		textColor: "text-red-900",
		bgLight: "bg-red-100",
	},
	{
		range: "0%",
		label: "Blind (No Light Perception)",
		description: "Complete vision loss. No functional vision in this eye.",
		color: "bg-gray-900",
		textColor: "text-gray-900",
		bgLight: "bg-gray-100",
	},
];

/* ─────────────────────────────────────────────
   Score bar component — reusable visual bar
   ───────────────────────────────────────────── */

function ScoreBar({
	score,
	size = "md",
}: { score: number; size?: "sm" | "md" }) {
	const height = size === "sm" ? "h-2" : "h-3";
	const color =
		score <= 10
			? "bg-emerald-500"
			: score <= 30
				? "bg-lime-500"
				: score <= 50
					? "bg-yellow-500"
					: score <= 70
						? "bg-orange-500"
						: "bg-red-500";

	return (
		<div
			className={`w-full ${height} bg-gray-200 rounded-full overflow-hidden`}
		>
			<div
				className={`${height} ${color} rounded-full transition-all`}
				style={{ width: `${score}%` }}
			/>
		</div>
	);
}

/* ─────────────────────────────────────────────
   Main Page Component
   ───────────────────────────────────────────── */

export default function ProposalPage() {
	return (
		<div className="min-h-screen bg-gray-50">
			{/* ── Header ── */}
			<div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-12">
				<div className="max-w-4xl mx-auto px-6">
					<p className="text-slate-400 text-sm font-medium mb-3 uppercase tracking-wider">
						Internal Proposal — For Clinician Feedback
					</p>
					<h1 className="text-4xl font-bold mb-4">
						How Should We Score Vision?
					</h1>
					<p className="text-xl text-slate-300 max-w-3xl leading-relaxed">
						We want to give every patient a single, easy-to-understand number
						that captures how well they can see — from perfect vision all the
						way to blindness. Here's our proposed approach.
					</p>
				</div>
			</div>

			<div className="max-w-4xl mx-auto px-6 py-12 space-y-16">
				{/* ── Section 1: The Big Idea ── */}
				<section>
					<h2 className="text-2xl font-bold text-gray-900 mb-2">
						The Big Idea
					</h2>
					<p className="text-gray-600 mb-6">
						Start with 100% — that's perfect vision. Each problem we detect
						takes points away. The final number tells you how much useful vision
						remains.
					</p>

					<div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
						{/* Visual scale */}
						<div className="flex items-stretch gap-0 h-16 rounded-xl overflow-hidden mb-6">
							<div className="flex-1 bg-emerald-500 flex items-center justify-center">
								<span className="text-white font-bold text-sm">100%</span>
							</div>
							<div className="flex-1 bg-lime-500" />
							<div className="flex-1 bg-yellow-500 flex items-center justify-center">
								<span className="text-white font-bold text-sm">50%</span>
							</div>
							<div className="flex-1 bg-orange-500" />
							<div className="flex-1 bg-red-500" />
							<div className="flex-1 bg-red-800" />
							<div className="flex-1 bg-gray-900 flex items-center justify-center">
								<span className="text-white font-bold text-sm">0%</span>
							</div>
						</div>

						<div className="flex justify-between text-sm text-gray-500 mb-8">
							<span>Perfect Vision</span>
							<span>Moderate Loss</span>
							<span>Blind</span>
						</div>

						{/* Plain english explanation */}
						<div className="grid md:grid-cols-3 gap-4">
							<div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
								<p className="font-semibold text-emerald-800">90 – 100%</p>
								<p className="text-sm text-emerald-700 mt-1">
									"You see just fine. Little to no issues detected."
								</p>
							</div>
							<div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200">
								<p className="font-semibold text-yellow-800">50 – 70%</p>
								<p className="text-sm text-yellow-700 mt-1">
									"Your vision has clear problems that affect daily life."
								</p>
							</div>
							<div className="p-4 bg-gray-100 rounded-xl border border-gray-300">
								<p className="font-semibold text-gray-800">0%</p>
								<p className="text-sm text-gray-600 mt-1">
									"No functional vision in this eye."
								</p>
							</div>
						</div>
					</div>
				</section>

				{/* ── Section 2: The Full Scale ── */}
				<section>
					<h2 className="text-2xl font-bold text-gray-900 mb-2">
						The Full Scale
					</h2>
					<p className="text-gray-600 mb-6">
						Seven levels from perfect vision to complete blindness.
					</p>

					<div className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden">
						{severityBands.map((band) => (
							<div
								key={band.label}
								className={`flex items-start gap-4 p-5 border-b border-gray-100 last:border-b-0 ${band.bgLight}`}
							>
								<div
									className={`w-3 h-3 rounded-full ${band.color} mt-1.5 flex-shrink-0`}
								/>
								<div className="flex-1">
									<div className="flex items-baseline gap-3">
										<span className="font-bold text-gray-900">
											{band.label}
										</span>
										<span className="text-sm text-gray-500 font-mono">
											{band.range}
										</span>
									</div>
									<p className="text-sm text-gray-600 mt-1">
										{band.description}
									</p>
								</div>
							</div>
						))}
					</div>
				</section>

				{/* ── Section 3: What Can Go Wrong ── */}
				<section>
					<h2 className="text-2xl font-bold text-gray-900 mb-2">
						What Can Go Wrong — And How We'd Score Each One
					</h2>
					<p className="text-gray-600 mb-6">
						There are many ways vision can be affected. Below is every type of
						problem our tool can detect, what it looks like, and how we'd turn
						it into a number.
					</p>

					<div className="space-y-6">
						{deficitTypes.map((deficit) => {
							const Illustration = deficit.illustration;
							return (
								<div
									key={deficit.id}
									className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden"
								>
									{/* Title bar */}
									<div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
										<h3 className="text-lg font-bold text-gray-900">
											{deficit.title}
										</h3>
									</div>

									<div className="p-6">
										{/* Two-column: illustration + description */}
										<div className="flex flex-col md:flex-row gap-6 mb-6">
											{/* Illustration */}
											<div className="md:w-1/3 bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center justify-center">
												<Illustration />
											</div>

											{/* Description */}
											<div className="md:w-2/3 space-y-3">
												<div>
													<p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
														What it is
													</p>
													<p className="text-gray-800">{deficit.whatItIs}</p>
												</div>
												<div>
													<p className="text-sm font-medium text-gray-500 uppercase tracking-wider">
														In everyday life
													</p>
													<p className="text-gray-800">{deficit.realLife}</p>
												</div>
											</div>
										</div>

										{/* How we score it */}
										<div className="bg-blue-50 rounded-xl p-4 border border-blue-200 mb-6">
											<p className="text-sm font-medium text-blue-800 mb-1">
												How we'd calculate the score
											</p>
											<p className="text-sm text-blue-700">
												{deficit.howWeScore}
											</p>
										</div>

										{/* Example levels */}
										<div className="space-y-2">
											<p className="text-sm font-medium text-gray-500">
												Example levels (0 = no problem, 100 = worst possible)
											</p>
											{deficit.examples.map((ex) => (
												<div key={ex.label} className="flex items-center gap-3">
													<div className="w-10 text-right font-mono text-sm font-bold text-gray-700">
														{ex.score}
													</div>
													<div className="flex-1">
														<ScoreBar score={ex.score} size="sm" />
													</div>
													<div className="w-48 flex-shrink-0">
														<span className="text-sm font-medium text-gray-800">
															{ex.label}
														</span>
														<span className="text-sm text-gray-500 ml-1">
															— {ex.note}
														</span>
													</div>
												</div>
											))}
										</div>
									</div>
								</div>
							);
						})}
					</div>
				</section>

				{/* ── Section 4: Putting It Together ── */}
				<section>
					<h2 className="text-2xl font-bold text-gray-900 mb-2">
						Putting It All Together
					</h2>
					<p className="text-gray-600 mb-6">
						Most patients have more than one problem. Here's how we combine them
						into one score.
					</p>

					<div className="bg-white rounded-2xl border-2 border-gray-200 p-8">
						{/* Step by step */}
						<div className="space-y-6">
							{/* Step 1 */}
							<div className="flex gap-4">
								<div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
									1
								</div>
								<div>
									<p className="font-semibold text-gray-900">
										Score each problem separately
									</p>
									<p className="text-gray-600 text-sm">
										Each of the 11 types above gets its own score from 0 to 100.
										If the patient has no problem in that area, it's 0.
									</p>
								</div>
							</div>

							{/* Step 2 */}
							<div className="flex gap-4">
								<div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
									2
								</div>
								<div>
									<p className="font-semibold text-gray-900">
										Average the deficits
									</p>
									<p className="text-gray-600 text-sm">
										Add up all the deficit scores and divide by the number of
										problem types. This gives us an overall "how bad is it"
										number.
									</p>
								</div>
							</div>

							{/* Step 3 */}
							<div className="flex gap-4">
								<div className="w-8 h-8 bg-slate-800 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
									3
								</div>
								<div>
									<p className="font-semibold text-gray-900">
										Subtract from 100
									</p>
									<p className="text-gray-600 text-sm">
										The final score is 100 minus the average deficit. Higher is
										better.
									</p>
								</div>
							</div>
						</div>

						{/* Worked example */}
						<div className="mt-8 p-6 bg-slate-50 rounded-xl border border-slate-200">
							<p className="font-semibold text-gray-900 mb-4">Worked Example</p>

							<div className="space-y-2 mb-6">
								{[
									{
										name: "Blurriness",
										score: 30,
										note: "moderately blurry, like needing stronger glasses",
									},
									{ name: "Contrast", score: 0, note: "no problems" },
									{
										name: "Blind Spots",
										score: 15,
										note: "small spot off to the side",
									},
									{ name: "Distortion", score: 0, note: "no problems" },
									{ name: "Wavy Vision", score: 0, note: "no problems" },
									{ name: "Double Vision", score: 0, note: "no problems" },
									{ name: "Size Difference", score: 0, note: "no problems" },
									{ name: "Color Shift", score: 0, note: "no problems" },
									{ name: "Tilted Vision", score: 0, note: "no problems" },
									{ name: "Brightness", score: 0, note: "no problems" },
									{ name: "Vacuum Effect", score: 0, note: "no problems" },
								].map((item) => (
									<div
										key={item.name}
										className="flex items-center gap-3 text-sm"
									>
										<span className="w-32 font-medium text-gray-700">
											{item.name}
										</span>
										<span
											className={`w-8 text-right font-mono font-bold ${item.score > 0 ? "text-orange-600" : "text-gray-400"}`}
										>
											{item.score}
										</span>
										<span className="text-gray-500">{item.note}</span>
									</div>
								))}
							</div>

							{/* Calculation */}
							<div className="border-t border-slate-300 pt-4">
								<div className="text-sm text-gray-600 space-y-1">
									<p>
										Average deficit = (30 + 15) / 11 = <strong>4.1</strong>
									</p>
									<p>
										Vision score = 100 - 4.1 ={" "}
										<strong className="text-emerald-700 text-lg">95.9%</strong>
									</p>
								</div>
								<div className="mt-4 p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
									<p className="text-2xl font-bold text-emerald-700">
										96% — Near-Perfect
									</p>
									<p className="text-sm text-emerald-600 mt-1">
										"This patient has some blur and a small blind spot, but
										overall vision is well preserved."
									</p>
								</div>
							</div>
						</div>

						{/* Alternative weighting note */}
						<div className="mt-6 p-4 bg-amber-50 rounded-xl border border-amber-200">
							<p className="text-sm font-medium text-amber-800">
								Open question: Should some problems count more?
							</p>
							<p className="text-sm text-amber-700 mt-1">
								For example, losing central vision (blur, distortion) arguably
								affects daily life more than a color shift. We could apply
								weights — like counting central problems 2x — but want your
								input first.
							</p>
						</div>
					</div>
				</section>

				{/* ── Section 5: Feedback Questions ── */}
				<section>
					<h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
						<QuestionMarkCircleIcon className="w-6 h-6 text-indigo-600" />
						We Need Your Feedback
					</h2>
					<p className="text-gray-600 mb-6">
						This is a starting point. We'd love your input on these questions
						before we build anything.
					</p>

					<div className="bg-gradient-to-br from-indigo-50 to-slate-50 rounded-2xl border-2 border-indigo-200 p-8">
						<div className="space-y-4">
							{[
								{
									q: "Does this scale make sense?",
									detail:
										"Is 0–100% intuitive? Would you prefer something different — like 1-10, or letter grades (A–F), or standard clinical notation (20/20)?",
								},
								{
									q: "Should central vision problems count more?",
									detail:
										"Blur and distortion in the center of your gaze arguably affect life more than peripheral issues. Should we weight them higher?",
								},
								{
									q: "Are the severity levels right?",
									detail:
										"We propose 7 levels from Perfect to Blind. Are these breakpoints in the right places? Should we add or remove any?",
								},
								{
									q: "What about location?",
									detail:
										"If blur only affects the upper half of vision, or a blind spot is off to the left — should that change the score? How much?",
								},
								{
									q: "How should distortion and warping be measured?",
									detail:
										"These are the hardest to quantify. Central vs. peripheral warping, radial vs. linear patterns — what matters most clinically?",
								},
								{
									q: "What score means 'take action'?",
									detail:
										"Below what score should a clinician be concerned? Is there a threshold where treatment should be considered?",
								},
							].map((item, index) => (
								<div
									key={item.q}
									className="flex gap-4 p-4 bg-white/70 rounded-xl"
								>
									<div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
										{index + 1}
									</div>
									<div>
										<p className="font-semibold text-gray-900">{item.q}</p>
										<p className="text-gray-600 text-sm">{item.detail}</p>
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* Footer */}
				<div className="text-center text-gray-500 text-sm py-8 border-t border-gray-200">
					<p>
						This is a proposal for discussion. Every formula and threshold shown
						here can be adjusted based on your feedback.
					</p>
					<p className="mt-2 text-gray-400">
						Advanced Vision Assessment · Internal Proposal · Feb 2026
					</p>
				</div>
			</div>
		</div>
	);
}
